import { useEffect, useState } from "react";

/** Hash routes, one per desktop page (the stacked widget in MainWindow). */
export type Route =
  | { name: "login" }
  | { name: "signup" }
  | { name: "admin" }
  | { name: "accept-invite"; token: string }
  | { name: "verify-email"; token: string }
  | { name: "forgot-password" }
  | { name: "reset-password"; token: string }
  | { name: "dashboard" }
  | { name: "annotate"; projectId: number; imageId?: number }
  | { name: "train"; projectId: number }
  | { name: "auto"; projectId: number; version?: number }
  | { name: "models"; projectId: number }
  | { name: "augment"; projectId: number; classId?: number; background?: boolean };

/** Routes a signed-out visitor may open: the sign-in pair and the email links. */
export const PUBLIC_ROUTES = new Set(["login", "signup", "verify-email", "forgot-password", "reset-password", "accept-invite"]);

export function parseRoute(hash: string): Route {
  const [path, query = ""] = hash.replace(/^#\/?/, "").split("?", 2);
  const token = new URLSearchParams(query).get("token") ?? "";
  const parts = path.split("/").filter(Boolean);
  const id = (s?: string) => (s && /^\d+$/.test(s) ? Number(s) : undefined);
  switch (parts[0]) {
    case "login": return { name: "login" };
    case "signup": return { name: "signup" };
    case "admin": return { name: "admin" };
    case "accept-invite": return { name: "accept-invite", token };
    case "verify-email": return { name: "verify-email", token };
    case "forgot-password": return { name: "forgot-password" };
    case "reset-password": return { name: "reset-password", token };
    case "annotate": { const p = id(parts[1]); return p ? { name: "annotate", projectId: p, imageId: id(parts[2]) } : { name: "dashboard" }; }
    case "train": { const p = id(parts[1]); return p ? { name: "train", projectId: p } : { name: "dashboard" }; }
    case "auto": {
      const p = id(parts[1]);
      const v = id(new URLSearchParams(query).get("version") ?? undefined);
      return p ? { name: "auto", projectId: p, ...(v ? { version: v } : {}) } : { name: "dashboard" };
    }
    case "models": { const p = id(parts[1]); return p ? { name: "models", projectId: p } : { name: "dashboard" }; }
    case "augment": {
      const p = id(parts[1]);
      const q = new URLSearchParams(query);
      const c = id(q.get("class") ?? undefined);
      // ?background=1: opened from the Train page's advice, on the Background pictures section.
      const b = q.get("background") === "1";
      return p ? { name: "augment", projectId: p, ...(c ? { classId: c } : {}), ...(b ? { background: true } : {}) } : { name: "dashboard" };
    }
    default: return { name: "dashboard" };
  }
}

export function hrefFor(r: Route): string {
  switch (r.name) {
    case "login": return "#/login";
    case "signup": return "#/signup";
    case "admin": return "#/admin";
    case "accept-invite": return `#/accept-invite?token=${encodeURIComponent(r.token)}`;
    case "verify-email": return `#/verify-email?token=${encodeURIComponent(r.token)}`;
    case "forgot-password": return "#/forgot-password";
    case "reset-password": return `#/reset-password?token=${encodeURIComponent(r.token)}`;
    case "dashboard": return "#/";
    case "annotate": return `#/annotate/${r.projectId}${r.imageId ? `/${r.imageId}` : ""}`;
    case "train": return `#/train/${r.projectId}`;
    case "auto": return `#/auto/${r.projectId}${r.version ? `?version=${r.version}` : ""}`;
    case "models": return `#/models/${r.projectId}`;
    case "augment": {
      const q = [r.classId ? `class=${r.classId}` : "", r.background ? "background=1" : ""].filter(Boolean).join("&");
      return `#/augment/${r.projectId}${q ? `?${q}` : ""}`;
    }
  }
}

export const navigate = (r: Route) => { window.location.hash = hrefFor(r); };

export function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.hash));
  useEffect(() => {
    const on = () => setRoute(parseRoute(window.location.hash));
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return route;
}
